import { z } from 'zod';
import { AdminReason, IsoDateTime, PackKey } from './fields.js';
/**
 * SSC Rankers League, early-bird and winner-claim contracts (ADR 0031, ADR 0032). Times are ISO strings
 * with an offset; money is whole rupees unless a name says otherwise. Prize amounts are `null`
 * (and prize wording absent) while the `grand_mock_prizes` flag is off.
 */
/** One place's prize in whole rupees. */
export const PrizeRupees = z.number().int().min(0).max(1_000_000);
const Prizes = z.array(PrizeRupees).max(10);
export const GrandMockStatusName = z.enum([
    'draft',
    'scheduled',
    'live',
    'ended',
    'results_published',
    'paid',
    'cancelled',
]);
// ── Public / website ─────────────────────────────────────────────────────────────────────────
/** One paper section: `minutes` is its own timer, or null when it shares the paper's clock. */
export const GrandMockSectionDto = z.object({
    key: z.string(),
    name: z.string(),
    questions: z.number(),
    /** Marks per question; negative = marks lost per wrong answer. */
    marks: z.number(),
    negative: z.number(),
    minutes: z.number().nullable(),
});
/** The SSC Rankers League card (website, Mini App, bot). */
export const GrandMockCardDto = z.object({
    id: z.uuid(),
    title: z.string(),
    examKey: z.string(),
    examName: z.string(),
    paperName: z.string().nullable(),
    questions: z.number().nullable(),
    totalMarks: z.number().nullable(),
    status: GrandMockStatusName,
    cancelReason: z.string().nullable(),
    startsAt: z.string(),
    joinClosesAt: z.string(),
    durationMin: z.number(),
    joinWindowMin: z.number(),
    resultsAt: z.string(),
    rulesVersion: z.string(),
    paused: z.boolean(),
    notices: z.array(z.object({ at: z.string(), kind: z.string(), text: z.string() })),
    /** Prize per place, 1st first; `null` while prizes are off. */
    prizesInr: z.array(z.number()).nullable(),
    /** Daily Challenge days a free learner needs (pack `streak_days`), counted since the last exam. */
    streakDays: z.number(),
    /** Whether those days must be in a row (a missed day resets the count to 0). */
    streakConsecutive: z.boolean().optional(),
    /** Paper sections for the instructions screen (no question content). */
    sections: z.array(GrandMockSectionDto),
    serverTime: z.string(),
});
export const GrandMockPublicDto = z.object({
    next: GrandMockCardDto.nullable(),
    streakEligibility: z.boolean(),
    /** Pack `grand_mock.streak_days` / `streak_consecutive` (older APIs omit them). */
    streakDays: z.number().optional(),
    streakConsecutive: z.boolean().optional(),
    prizesOn: z.boolean(),
});
export const GrandMockRulesDto = z.object({
    version: z.string(),
    title: z.string(),
    sections: z.array(z.object({
        id: z.string(),
        title: z.string(),
        paragraphs: z.array(z.string()),
        bullets: z.array(z.string()).optional(),
        ordered: z.boolean().optional(),
    })),
    agreements: z.array(z.object({ key: z.enum(['age', 'india', 'rules']), label: z.string() })),
});
/** "X of 50 early-bird spots left". `null` when the product has no early-bird cap. */
export const EarlyBirdCounterDto = z.object({
    max: z.number(),
    sold: z.number(),
    held: z.number(),
    left: z.number(),
    open: z.boolean(),
    endedAt: z.string().nullable(),
    endedReason: z.string().nullable(),
});
export const EarlyBirdPublicDto = z.object({ earlyBird: EarlyBirdCounterDto.nullable() });
/** The Free plan card shown first on every pricing page. */
export const FreePlanCardDto = z.object({
    name: z.string(),
    blurb: z.string().nullable(),
    features: z.array(z.looseObject({ key: z.string().optional(), text: z.string().optional() })),
});
/** `/web/claim/:token` (winner details link). */
export const ClaimViewDto = z.object({
    title: z.string(),
    place: z.number(),
    prizeInr: z.number(),
    expiresAt: z.string().nullable(),
    /** Days the link works / days we take to pay after correct details (from the exam pack). */
    claimDays: z.number().int(),
    payoutDays: z.number().int(),
    /** The PAN field is shown only when this is true (OFF by default). */
    panField: z.boolean(),
    /** Photo upload is offered only when this is true. */
    photo: z.boolean(),
    maxPhotoBytes: z.number(),
});
export const ClaimSubmitRequest = z
    .object({
    name: z.string().trim().min(2).max(80),
    upi: z.string().trim().min(3).max(80),
    pan: z.string().trim().max(10).optional(),
    publishConsent: z
        .union([z.boolean(), z.enum(['true', 'false'])])
        .transform((v) => v === true || v === 'true'),
})
    .strict();
export const ClaimSubmitDto = z.object({ ok: z.literal(true) });
// ── Mini App (learner) ───────────────────────────────────────────────────────────────────────
export const StreakProgressDto = z.object({
    required: z.number(),
    done: z.number(),
    broken: z.boolean(),
    qualified: z.boolean(),
    dueToday: z.boolean(),
    missedOn: z.string().nullable(),
});
export const GrandMockEligibilityDto = z.union([
    z.object({ eligible: z.literal(true), via: z.enum(['pass', 'streak', 'open']) }),
    z.object({
        eligible: z.literal(false),
        reason: z.enum(['needs_pass', 'streak_in_progress', 'streak_broken']),
        streak: StreakProgressDto.nullable(),
    }),
]);
/** `GET /app/grand-mock`: the card plus this learner's state ("Streak 9/14" = streak.done/required). */
export const LearnerGrandMockDto = z.object({
    /** The `mocks_enabled` flag: false = the League is hidden and a link to it says "not available yet". */
    mocksEnabled: z.boolean(),
    grandMock: GrandMockCardDto.nullable(),
    /** The newest SSC Rankers League with published results (any age); open it with `GET /app/grand-mock/:id/results`. */
    latestResults: z
        .object({ id: z.uuid(), title: z.string(), startsAt: z.string(), resultsAt: z.string() })
        .nullable(),
    me: z
        .object({
        eligibility: GrandMockEligibilityDto,
        hasPass: z.boolean(),
        streak: StreakProgressDto.nullable(),
        reminded: z.boolean(),
        agreed: z.boolean(),
        /** When the current rules version was agreed; null if not yet. */
        agreedAt: z.string().nullable(),
        attempt: z.object({ id: z.uuid(), status: z.string() }).nullable(),
        canStart: z.boolean(),
        resultsOut: z.boolean(),
    })
        .nullable(),
});
export const GrandMockAgreeRequest = z
    .object({
    age: z.boolean(),
    india: z.boolean(),
    rules: z.boolean(),
    rulesVersion: z.string().min(1).max(40),
})
    .strict();
export const GrandMockAgreeDto = z.object({ agreedAt: z.string(), rulesVersion: z.string() });
export const GrandMockRemindDto = z.object({
    grandMockId: z.uuid(),
    title: z.string(),
    startsAt: z.string(),
});
/** `device` is an optional SHA-256 hex of a client device hint (never an identifier we can read). */
export const GrandMockStartRequest = z
    .object({
    device: z
        .string()
        .regex(/^[a-f0-9]{64}$/)
        .optional(),
})
    .strict();
export const GrandMockSignalRequest = z
    .object({
    switches: z.number().int().min(0).max(5),
    hiddenMs: z.number().int().min(0).max(600_000),
})
    .strict();
export const GrandMockOkDto = z.object({ ok: z.boolean() });
export const GrandMockLearnerResultsDto = z.looseObject({
    grandMock: GrandMockCardDto,
    released: z.boolean(),
});
// ── Admin: SSC Rankers League ────────────────────────────────────────────────────────────────────────
export const GrandMockViewDto = z.looseObject({
    id: z.uuid(),
    productId: z.uuid(),
    mockId: z.uuid(),
    examKey: z.string(),
    examName: z.string(),
    paperName: z.string(),
    title: z.string(),
    status: GrandMockStatusName,
    editable: z.boolean(),
    startsAt: z.string(),
    joinClosesAt: z.string(),
    endsAt: z.string(),
    joinWindowMin: z.number(),
    durationMin: z.number(),
    announceAt: z.string(),
    resultsAt: z.string(),
    prizesInr: z.array(z.number()),
    unclaimedPolicy: z.enum(['next_rank', 'rollover']),
    rulesVersion: z.string(),
    extendedMin: z.number(),
    paused: z.boolean(),
    cancelReason: z.string().nullable(),
    approvedAt: z.string().nullable(),
    publishedAt: z.string().nullable(),
    sentStages: z.array(z.string()),
    questionSet: z.object({
        built: z.boolean(),
        questions: z.number(),
        shortfalls: z.array(z.unknown()),
        frozen: z.boolean(),
    }),
});
export const GrandMockListDto = z.object({ grandMocks: z.array(GrandMockViewDto) });
export const GrandMockDefaultsDto = z.looseObject({
    suggested: z.object({ startsAt: z.string(), announceAt: z.string(), resultsAt: z.string() }),
    papers: z.array(z.looseObject({ examKey: z.string(), name: z.string() })),
});
export const CreateGrandMockRequest = z
    .object({
    examKey: PackKey,
    stageKey: PackKey.optional(),
    paperKey: PackKey.optional(),
    title: z.string().trim().min(3).max(160).optional(),
    startsAt: IsoDateTime,
    joinWindowMin: z.number().int().min(0).max(120).optional(),
    announceAt: IsoDateTime.optional(),
    resultsAt: IsoDateTime.optional(),
    prizesInr: Prizes.optional(),
    unclaimedPolicy: z.enum(['next_rank', 'rollover']).optional(),
    /** Write a fresh hard set for this league and build the paper from it (ADR 0048). */
    freshSet: z.boolean().optional(),
})
    .strict();
export const UpdateGrandMockRequest = z
    .object({
    title: z.string().trim().min(3).max(160).optional(),
    startsAt: IsoDateTime.optional(),
    joinWindowMin: z.number().int().min(0).max(120).optional(),
    announceAt: IsoDateTime.optional(),
    resultsAt: IsoDateTime.optional(),
    prizesInr: Prizes.optional(),
    unclaimedPolicy: z.enum(['next_rank', 'rollover']).optional(),
})
    .strict();
export const PostponeGrandMockRequest = z
    .object({ startsAt: IsoDateTime, resultsAt: IsoDateTime.optional(), reason: AdminReason })
    .strict();
export const GrandMockReasonRequest = z.object({ reason: AdminReason }).strict();
export const GrandMockExtendRequest = z
    .object({ minutes: z.number().int().min(1).max(60), reason: AdminReason })
    .strict();
export const GrandMockResultsQuery = z
    .object({
    flaggedOnly: z
        .enum(['true', 'false'])
        .transform((v) => v === 'true')
        .optional(),
    limit: z.coerce.number().int().min(1).max(5000).optional(),
})
    .strict();
export const ReadinessCheckDto = z.object({
    key: z.string(),
    level: z.enum(['ok', 'warn', 'blocker']),
    title: z.string(),
    detail: z.string(),
});
/** Plain-language "is it ready?" (blockers stop scheduling; warnings do not). */
/** A league's fresh hard set (ADR 0048): per section, questions needed and ready to use. */
export const GrandMockFreshSetDto = z.looseObject({
    available: z.boolean(),
    started: z.boolean(),
    sections: z.array(z.looseObject({
        topicRoot: z.string(),
        name: z.string(),
        needed: z.number(),
        ready: z.number(),
    })),
    jobs: z
        .looseObject({
        total: z.number(),
        open: z.number(),
        failed: z.number(),
        written: z.number(),
        retired: z.number(),
    })
        .nullable(),
    complete: z.boolean(),
});
export const GrandMockReadinessDto = z.looseObject({
    grandMock: GrandMockViewDto,
    ready: z.boolean(),
    summary: z.string(),
    checks: z.array(ReadinessCheckDto),
    passHolders: z.number(),
    freshSet: GrandMockFreshSetDto.optional(),
});
export const GrandMockResultsDto = z.looseObject({
    grandMock: GrandMockViewDto,
    /** The random-draw seed (shown after the exam ends so anyone can re-check a draw). */
    tieSeed: z.string().nullable(),
    totals: z.object({ total: z.number(), flagged: z.number(), disqualified: z.number() }),
    rows: z.array(z.looseObject({
        id: z.uuid(),
        rank: z.number().nullable(),
        name: z.string(),
        score: z.number(),
        maxScore: z.number(),
    })),
});
export const GrandMockMonitorDto = z.looseObject({ grandMock: GrandMockViewDto });
export const GrandMockReportDto = z.looseObject({});
export const GrandMockMonthlyReportQuery = z
    .object({ months: z.coerce.number().int().min(1).max(24).optional() })
    .strict();
// Winners and payouts (Owner only; step-up 2FA).
export const GrandMockPayoutsDto = z.looseObject({
    grandMockId: z.uuid(),
    status: GrandMockStatusName,
    prizesAtPublish: z.boolean(),
    unclaimedPolicy: z.string(),
    rows: z.array(z.looseObject({
        id: z.uuid(),
        place: z.number(),
        rank: z.number().nullable(),
        prizeInr: z.number(),
        name: z.string(),
        status: z.enum(['pending', 'link_sent', 'claimed', 'paid', 'expired', 'forfeited']),
        hasDetails: z.boolean(),
        paidAt: z.string().nullable(),
        paidRef: z.string().nullable(),
    })),
});
export const WinnerDetailsDto = z.object({
    name: z.string(),
    upi: z.string(),
    pan: z.string().nullable(),
    hasPhoto: z.boolean(),
    publishConsent: z.boolean(),
    submittedAt: z.string(),
});
export const MarkPaidRequest = z
    .object({
    reference: z
        .string()
        .trim()
        .min(4)
        .max(64)
        .regex(/^[A-Za-z0-9._\-/ ]+$/),
})
    .strict();
export const UnclaimedRequest = z.object({ policy: z.enum(['next_rank', 'rollover']) }).strict();
// ── Admin: early-bird ────────────────────────────────────────────────────────────────────────
export const EarlyBirdAdminDto = z.object({
    earlyBird: EarlyBirdCounterDto.nullable(),
    buyers: z.array(z.looseObject({
        id: z.uuid(),
        slot: z.number().nullable(),
        status: z.string(),
        isTest: z.boolean(),
        testReason: z.string().nullable(),
        refunded: z.boolean(),
        planKey: z.string(),
        rail: z.string().nullable().optional(),
        amount: z.number(),
        currency: z.string(),
    })),
});
export const EarlyBirdLimitRequest = z
    .object({ max: z.number().int().min(0).max(100_000), reason: AdminReason })
    .strict();
export const EarlyBirdEndRequest = z.object({ reason: AdminReason }).strict();
export const EarlyBirdMarkTestRequest = z.object({ reason: AdminReason }).strict();
// ── Admin: support queue ─────────────────────────────────────────────────────────────────────
export const SupportKind = z.enum(['support', 'paysupport', 'copyright', 'grand_mock']);
export const SupportCountsDto = z.object({ open: z.record(z.string(), z.number()) });
export const CannedReplyDto = z.object({
    id: z.uuid(),
    title: z.string(),
    body: z.string(),
    kinds: z.array(z.string()),
    position: z.number(),
});
export const CannedReplyListDto = z.object({ replies: z.array(CannedReplyDto) });
export const CannedReplyRequest = z
    .object({
    title: z.string().trim().min(3).max(80),
    body: z.string().trim().min(3).max(3000),
    kinds: z.array(SupportKind).max(4).optional(),
    position: z.number().int().min(0).max(1000).optional(),
})
    .strict();
export const CannedReplyIdDto = z.object({ id: z.uuid() });
//# sourceMappingURL=grand-mock.js.map